import { useEffect, useState } from 'react';
import { apiFetch } from '../config/api';

function pluralize(count, singular, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function summarizeRecipientsForBulkSend(recipients = [], isStaff = false) {
  return recipients.reduce((summary, recipient) => {
    const hasPhone = Boolean(String((isStaff ? recipient.mobile_phone : recipient.phone) || '').trim());
    const hasEmail = !isStaff || Boolean(String(recipient.school_email || '').trim());
    const isReachable = hasPhone && hasEmail;
    const hasPassword = Boolean(recipient.has_password);

    summary.total += 1;
    if (isReachable) summary.withPhone += 1;
    else summary.missingPhone += 1;
    if (isStaff && hasPhone && !hasEmail) summary.missingEmail += 1;

    if (isReachable && hasPassword) summary.alreadyOnboarded += 1;
    if (isReachable && !hasPassword) summary.needsOnboarding += 1;

    return summary;
  }, {
    total: 0,
    withPhone: 0,
    needsOnboarding: 0,
    alreadyOnboarded: 0,
    missingPhone: 0,
    missingEmail: 0,
  });
}

const ACTIVE_BULK_STATUSES = new Set(['queued', 'running', 'paused']);

function isBulkJobActive(job) {
  return ACTIVE_BULK_STATUSES.has(job?.status);
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDurationSeconds(seconds) {
  const safeSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
  if (safeSeconds < 60) return safeSeconds <= 5 ? 'now' : '<1 min';

  const minutes = Math.ceil(safeSeconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes
    ? `${hours} hr ${remainingMinutes} min`
    : `${hours} hr`;
}

function formatWindowSeconds(seconds) {
  const safeSeconds = Math.max(1, Number(seconds) || 1);

  if (safeSeconds % 3600 === 0) {
    const hours = safeSeconds / 3600;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  if (safeSeconds % 60 === 0) {
    const minutes = safeSeconds / 60;
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  return `${safeSeconds} seconds`;
}

function formatRatePolicy(policy) {
  if (!policy?.limit || !policy?.window_seconds) return '';
  const noun = policy.limit === 1 ? 'message' : 'messages';
  return `${policy.limit} ${noun} every ${formatWindowSeconds(policy.window_seconds)}`;
}

function secondsUntil(value, now) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, Math.ceil((timestamp - now) / 1000));
}

function bulkProgressPercent(job) {
  const fallbackTargetCount = Number(job?.target_count) || 0;
  const fallbackProcessed = Number(job?.processed_count ?? job?.current_index) || 0;
  const rawPercent = Number.isFinite(Number(job?.progress_percent))
    ? Number(job.progress_percent)
    : fallbackTargetCount > 0
      ? (fallbackProcessed / fallbackTargetCount) * 100
      : 100;

  return Math.max(0, Math.min(100, Math.round(rawPercent)));
}

function bulkEtaText(job, now) {
  if (!job) return '';

  if (!isBulkJobActive(job)) {
    if (job.status === 'completed' || job.status === 'completed_with_failures') {
      return job.completed_at ? `Done ${formatDateTime(job.completed_at)}` : 'Done';
    }
    return '';
  }

  const seconds = secondsUntil(job.estimated_completion_at, now);
  if (seconds === null) return '';

  const etaTime = formatDateTime(job.estimated_completion_at);
  const remaining = formatDurationSeconds(seconds);
  return etaTime ? `ETA ${etaTime} (${remaining})` : `ETA ${remaining}`;
}

function bulkNextBatchText(job, now) {
  if (!isBulkJobActive(job) || !job?.next_batch_at || !job?.remaining) return '';
  const seconds = secondsUntil(job.next_batch_at, now);
  if (seconds === null || seconds <= 5) return 'Next batch now';
  return `Next batch in ${formatDurationSeconds(seconds)}`;
}

function bulkStatusLabel(job) {
  switch (job?.status) {
    case 'queued':
      return 'Queued';
    case 'running':
      return 'Sending';
    case 'paused':
      return 'Paused';
    case 'completed_with_failures':
      return 'Complete with failures';
    case 'completed':
      return 'Complete';
    case 'failed':
      return 'Failed';
    default:
      return 'Result';
  }
}

function bulkPauseMessage(job) {
  const retryTime = formatDateTime(job?.next_retry_at);
  const retryText = retryTime ? ` The next batch starts after ${retryTime}.` : '';

  if (job?.rate_limit?.source === 'local_rate_window') {
    const rateText = formatRatePolicy(job.rate_limit) || formatRatePolicy(job.rate_policy) || 'the configured WhatsApp cadence';
    return `WhatsApp cadence reached (${rateText}).${retryText}`;
  }

  return `WhatsApp provider limit reached.${retryText}`;
}

export default function SchoolOnboardingWhatsAppField({
  schoolId,
  token,
  disabled = false,
  audience = 'parent',
}) {
  const isStaff = audience === 'staff';
  const recipientLabel = isStaff ? 'staff member' : 'parent';
  const recipientAccountLabel = isStaff ? 'staff account' : 'parent account';
  const bulkPath = isStaff ? '/whatsapp/staff/onboarding/bulk' : '/whatsapp/onboarding/bulk';
  const [parentCounts, setParentCounts] = useState(() => ({
    loading: true,
    total: 0,
    withPhone: 0,
    needsOnboarding: 0,
    alreadyOnboarded: 0,
    missingPhone: 0,
    missingEmail: 0,
  }));
  const [parentCountError, setParentCountError] = useState('');
  const [includeAlreadyOnboarded, setIncludeAlreadyOnboarded] = useState(false);
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkRefreshKey, setBulkRefreshKey] = useState(0);
  const [bulkNow, setBulkNow] = useState(Date.now());

  useEffect(() => {
    setBulkResult(null);
    setBulkError('');
    setIncludeAlreadyOnboarded(false);
  }, [schoolId]);

  useEffect(() => {
    let cancelled = false;

    setParentCounts((current) => ({ ...current, loading: true }));
    setParentCountError('');

    apiFetch(isStaff ? `/staff?school_id=${schoolId}&is_active=true` : `/contacts?school_id=${schoolId}&type=parent`, {}, token)
      .then((result) => {
        if (cancelled) return;
        setParentCounts({
          loading: false,
          ...summarizeRecipientsForBulkSend(result.data || [], isStaff),
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setParentCounts({
          loading: false,
          total: 0,
          withPhone: 0,
          needsOnboarding: 0,
          alreadyOnboarded: 0,
          missingPhone: 0,
          missingEmail: 0,
        });
        setParentCountError(err.message || `Could not load ${isStaff ? 'staff' : 'parent'} counts`);
      });

    return () => {
      cancelled = true;
    };
  }, [bulkRefreshKey, isStaff, schoolId, token]);

  const bulkTargetCount = includeAlreadyOnboarded
    ? parentCounts.withPhone
    : parentCounts.needsOnboarding;
  const activeBulkJob = isBulkJobActive(bulkResult);
  const bulkButtonLabel = activeBulkJob
    ? bulkStatusLabel(bulkResult)
    : `Send to ${bulkTargetCount}`;
  const progressPercent = bulkProgressPercent(bulkResult);
  const etaText = bulkEtaText(bulkResult, bulkNow);
  const nextBatchText = bulkNextBatchText(bulkResult, bulkNow);
  const ratePolicyText = formatRatePolicy(bulkResult?.rate_policy);

  useEffect(() => {
    if (!activeBulkJob) return undefined;

    setBulkNow(Date.now());
    const interval = setInterval(() => setBulkNow(Date.now()), 10000);

    return () => clearInterval(interval);
  }, [activeBulkJob, bulkResult?.job_id, bulkResult?.status]);

  useEffect(() => {
    if (!schoolId) return undefined;

    let cancelled = false;

    apiFetch(`${bulkPath}/active?school_id=${schoolId}`, {}, token)
      .then((result) => {
        if (cancelled || !result.data) return;
        setBulkResult(result.data);
        setBulkSending(isBulkJobActive(result.data));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [bulkPath, schoolId, token]);

  useEffect(() => {
    const jobId = bulkResult?.job_id;
    const status = bulkResult?.status;
    if (!jobId || !ACTIVE_BULK_STATUSES.has(status)) return undefined;

    let cancelled = false;
    let refreshedAfterTerminal = false;

    const poll = async () => {
      try {
        const result = await apiFetch(`${bulkPath}/${jobId}`, {}, token);
        if (cancelled) return;

        const job = result.data;
        setBulkNow(Date.now());
        setBulkResult(job);
        setBulkSending(isBulkJobActive(job));

        if (!isBulkJobActive(job) && !refreshedAfterTerminal) {
          refreshedAfterTerminal = true;
          setBulkRefreshKey((current) => current + 1);
        }
      } catch (err) {
        if (!cancelled) setBulkError(err.message || 'Could not refresh onboarding WhatsApp job');
      }
    };

    const firstPoll = setTimeout(poll, 1000);
    const interval = setInterval(poll, status === 'paused' ? 15000 : 3000);

    return () => {
      cancelled = true;
      clearTimeout(firstPoll);
      clearInterval(interval);
    };
  }, [bulkPath, bulkResult?.job_id, bulkResult?.status, token]);

  const handleBulkSend = async () => {
    if (activeBulkJob) return;
    if (bulkTargetCount < 1) return;

    const targetDescription = includeAlreadyOnboarded
      ? pluralize(bulkTargetCount, recipientAccountLabel)
      : `${pluralize(bulkTargetCount, `unclaimed ${recipientAccountLabel}`)}`;

    if (!window.confirm(`Send onboarding WhatsApp to ${targetDescription}? ${isStaff ? 'Staff members' : 'Parents'} will be told to download the ${isStaff ? 'staff ' : ''}app and claim their account from the first screen. If the WhatsApp provider limit is reached, sending will pause and resume automatically.`)) {
      return;
    }

    setBulkSending(true);
    setBulkError('');
    setBulkResult(null);

    try {
      const result = await apiFetch(bulkPath, {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          force: includeAlreadyOnboarded,
        }),
      }, token);

      setBulkResult(result.data);
      setBulkNow(Date.now());
      setBulkSending(isBulkJobActive(result.data));
      if (!isBulkJobActive(result.data)) setBulkRefreshKey((current) => current + 1);
    } catch (err) {
      setBulkError(err.message || 'Could not send onboarding WhatsApp messages');
      setBulkSending(false);
    }
  };

  return (
    <div className="ssp-whatsapp-field">
      <div className="ssp-email-bulk-card ssp-whatsapp-bulk-card">
        <div className="ssp-email-bulk-header">
          <div>
            <h3>Send Onboarding WhatsApp to {isStaff ? 'Staff' : 'Parents'}</h3>
            <p>
              {parentCounts.loading
                ? `Loading ${isStaff ? 'staff' : 'parent'} accounts...`
                : `${pluralize(parentCounts.total, recipientAccountLabel)} found. ${pluralize(parentCounts.needsOnboarding, recipientLabel)} can receive WhatsApp onboarding now.`}
            </p>
          </div>
          <button
            type="button"
            className="sa-btn sa-btn-primary"
            onClick={handleBulkSend}
            disabled={disabled || bulkSending || activeBulkJob || parentCounts.loading || bulkTargetCount < 1}
          >
            {activeBulkJob ? bulkButtonLabel : (bulkSending ? 'Sending...' : bulkButtonLabel)}
          </button>
        </div>

        <label className="ssp-email-bulk-toggle">
          <input
            type="checkbox"
            checked={includeAlreadyOnboarded}
            onChange={(event) => {
              setIncludeAlreadyOnboarded(event.target.checked);
              setBulkError('');
              setBulkResult(null);
            }}
            disabled={disabled || bulkSending || activeBulkJob || parentCounts.loading}
          />
          <span>Include {pluralize(parentCounts.alreadyOnboarded, recipientLabel)} who already claimed their account</span>
        </label>

        {parentCounts.missingPhone - parentCounts.missingEmail > 0 ? (
          <div className="ssp-email-bulk-note">
            {pluralize(parentCounts.missingPhone - parentCounts.missingEmail, recipientLabel)} skipped because no phone number is saved.
          </div>
        ) : null}
        {parentCounts.missingEmail > 0 ? (
          <div className="ssp-email-bulk-note">{pluralize(parentCounts.missingEmail, recipientLabel)} skipped because no staff email is saved.</div>
        ) : null}
        {parentCountError ? <div className="sa-error ssp-email-error">{parentCountError}</div> : null}
        {bulkError ? <div className="sa-error ssp-email-error">{bulkError}</div> : null}

        {bulkResult ? (
          <div className="ssp-email-bulk-result">
            <strong>
              {bulkStatusLabel(bulkResult)}. Sent {bulkResult.summary?.sent || 0}. Skipped {bulkResult.summary?.skipped || 0}. Failed {bulkResult.summary?.failed || 0}. Pending {bulkResult.summary?.pending || 0}.
            </strong>
            {(bulkResult.target_count || 0) > 0 ? (
              <div className="ssp-email-bulk-progress-wrap">
                <div
                  className="ssp-email-bulk-progress-bar"
                  role="progressbar"
                  aria-label="Bulk onboarding WhatsApp send progress"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={progressPercent}
                >
                  <div className="ssp-email-bulk-progress-fill" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="ssp-email-bulk-progress-meta">
                  <span>Processing {bulkResult.processed_count ?? bulkResult.current_index ?? 0} of {bulkResult.target_count || 0} send targets</span>
                  {etaText ? <span className="ssp-email-bulk-eta">{etaText}</span> : null}
                </div>
                {nextBatchText || ratePolicyText ? (
                  <div className="ssp-email-bulk-progress-submeta">
                    {nextBatchText ? <span>{nextBatchText}</span> : null}
                    {ratePolicyText ? <span>{ratePolicyText}</span> : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            {bulkResult.status === 'paused' ? (
              <div className="ssp-email-bulk-progress">
                {bulkPauseMessage(bulkResult)}
              </div>
            ) : null}
            {bulkResult.status === 'failed' && bulkResult.error ? (
              <div className="ssp-email-bulk-failures">{bulkResult.error}</div>
            ) : null}
            {bulkResult.failures?.length ? (
              <div className="ssp-email-bulk-failures">
                {bulkResult.failures.slice(0, 4).map((failure) => (
                  <div key={failure.contact_id || failure.staff_id}>
                    {failure.name}: {failure.message}
                  </div>
                ))}
                {bulkResult.failures.length > 4 ? <div>+{bulkResult.failures.length - 4} more failures</div> : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
